import { useEffect, useState, useCallback } from 'react';
import { initView } from 'dingtalk-docs-cool-app';
import './App.css';

// 后端服务地址，开发时可改为本地地址
const API_BASE = process.env.REACT_APP_API_BASE || 'https://dmdb.ccqq.net:13306';

type Step = 'connect' | 'select';

interface TableInfo {
  name: string;
  rows: number;
  comment: string;
}

function App() {
  // 连接配置
  const [host, setHost] = useState('');
  const [port, setPort] = useState('3306');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // 步骤控制
  const [step, setStep] = useState<Step>('connect');

  // 数据库 & 表
  const [databases, setDatabases] = useState<string[]>([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState('');

  // 状态
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 初始化 iframe 环境
  useEffect(() => {
    initView({
      onReady: () => {
        console.log('initView ready');
      },
      onError: (e) => {
        // 非钉钉环境下（本地开发调试）忽略 notInDingTalk 错误
        if (e?.message?.includes?.('notInDingTalk') || String(e).includes('notInDingTalk')) {
          console.warn('当前不在钉钉环境中，部分功能（如 saveConfigAndGoNext）不可用，但页面可以正常调试');
        } else {
          console.error('initView error:', e);
        }
      },
    });
  }, []);

  // 获取连接参数
  const getConnParams = useCallback(() => ({
    host,
    port: parseInt(port, 10),
    username,
    password,
  }), [host, port, username, password]);

  // 测试连接并获取数据库列表
  const handleConnect = async () => {
    if (!host || !username || !password) {
      setError('请填写完整的连接信息');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/databases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(getConnParams()),
      });
      const data = await res.json();
      if (data.code !== 0) {
        setError(data.msg || '连接失败');
        return;
      }
      setDatabases(data.data.databases);
      setStep('select');
    } catch (e: any) {
      setError('连接服务器失败，请检查后端服务是否启动');
    } finally {
      setLoading(false);
    }
  };

  // 选择数据库后获取表列表
  const handleSelectDb = async (db: string) => {
    setSelectedDb(db);
    setSelectedTable('');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...getConnParams(), database: db }),
      });
      const data = await res.json();
      if (data.code !== 0) {
        setError(data.msg || '获取数据表失败');
        return;
      }
      setTables(data.data.tables);
    } catch (e: any) {
      setError('获取数据表失败');
    } finally {
      setLoading(false);
    }
  };

  // 下一步 - 保存配置并进入字段配置
  const goNext = async () => {
    if (!selectedDb || !selectedTable) {
      setError('请选择数据库和数据表');
      return;
    }
    const config = {
      host,
      port: parseInt(port, 10),
      username,
      password,
      database: selectedDb,
      table: selectedTable,
    };
    try {
      // 检查是否在钉钉环境中
      if (typeof Dingdocs !== 'undefined' && Dingdocs?.base?.host) {
        await (Dingdocs.base.host as any).saveConfigAndGoNext(config);
      } else {
        // 非钉钉环境，打印配置用于调试
        console.log('saveConfigAndGoNext config:', JSON.stringify(config, null, 2));
        setError('当前不在钉钉环境中，无法调用 saveConfigAndGoNext。配置已打印到控制台。');
      }
    } catch (e: any) {
      setError('保存配置失败: ' + (e.message || '未知错误'));
    }
  };

  // 返回连接配置
  const goBack = () => {
    setStep('connect');
    setError('');
  };

  return (
    <div className="app-container">
      {/* 头部 */}
      <div className="app-header">
        <div className="app-logo">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <ellipse cx="12" cy="6" rx="8" ry="3" />
            <path d="M4 6v6c0 1.657 3.582 3 8 3s8-1.343 8-3V6" />
            <path d="M4 12v6c0 1.657 3.582 3 8 3s8-1.343 8-3v-6" />
          </svg>
          <span>MySQL 数据源</span>
        </div>
        {step === 'connect' && (
          <div className="app-step-badge">1/2 连接配置</div>
        )}
        {step === 'select' && (
          <div className="app-step-badge">2/2 选择数据</div>
        )}
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="error-banner">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
          <button className="error-close" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {/* 步骤一：连接配置 */}
      {step === 'connect' && (
        <div className="step-content">
          <div className="form-group">
            <label className="form-label">主机地址</label>
            <input
              className="form-input"
              placeholder="例如: 127.0.0.1 或 db.example.com"
              value={host}
              onChange={(e) => setHost(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">端口</label>
            <input
              className="form-input"
              placeholder="3306"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              type="number"
            />
          </div>

          <div className="form-group">
            <label className="form-label">用户名</label>
            <input
              className="form-input"
              placeholder="数据库用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">密码</label>
            <div className="password-wrapper">
              <input
                className="form-input"
                placeholder="数据库密码"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                className="toggle-password"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? '隐藏密码' : '显示密码'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            className="btn btn-primary btn-full"
            onClick={handleConnect}
            disabled={loading}
          >
            {loading ? (
              <span className="btn-loading">
                <span className="spinner"></span>
                连接中...
              </span>
            ) : (
              '连接数据库'
            )}
          </button>
        </div>
      )}

      {/* 步骤二：选择数据库和数据表 */}
      {step === 'select' && (
        <div className="step-content">
          <div className="form-group">
            <label className="form-label">选择数据库</label>
            <select
              className="form-select"
              value={selectedDb}
              onChange={(e) => handleSelectDb(e.target.value)}
            >
              <option value="">-- 请选择数据库 --</option>
              {databases.map((db) => (
                <option key={db} value={db}>{db}</option>
              ))}
            </select>
          </div>

          {selectedDb && (
            <div className="form-group">
              <label className="form-label">
                选择数据表
                {tables.length > 0 && (
                  <span className="table-count">{tables.length} 张表</span>
                )}
              </label>
              {loading ? (
                <div className="table-loading">
                  <span className="spinner"></span>
                  <span>加载中...</span>
                </div>
              ) : tables.length === 0 ? (
                <div className="table-empty">该数据库暂无数据表</div>
              ) : (
                <div className="table-list">
                  {tables.map((t) => (
                    <div
                      key={t.name}
                      className={`table-item ${selectedTable === t.name ? 'selected' : ''}`}
                      onClick={() => setSelectedTable(t.name)}
                    >
                      <div className="table-item-left">
                        <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" className="table-icon">
                          <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
                        </svg>
                        <div className="table-item-info">
                          <span className="table-name">{t.name}</span>
                          {t.comment && <span className="table-comment">{t.comment}</span>}
                        </div>
                      </div>
                      <span className="table-rows">{t.rows} 行</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="btn-group">
            <button className="btn btn-secondary" onClick={goBack}>
              返回
            </button>
            <button
              className="btn btn-primary"
              onClick={goNext}
              disabled={!selectedDb || !selectedTable || loading}
            >
              下一步
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
